import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { FC } from 'react';

import { RosterRequestQuery } from 'generated/graphql';
import SendConfirmationEmail from 'pages/BookingConfirmation/components/SendConfirmationEmail';
import { formatCents, getPricingInfo, PLUS_TAX_TEXT } from 'utilities/money';
import { formatRoomQuantity } from 'utilities/roomTypeFormatter';

import BookingLineItems from './BookingLineItems';
import THSPolicyVerbiage from './THSPolicyVerbiage';

type RosterRequestReservation = NonNullable<
  RosterRequestQuery['rosterRequest']['order']
>['reservations'][0];

type BookingConfirmationCardProps = {
  reservation: RosterRequestReservation;
};

const BookingConfirmationCard: FC<BookingConfirmationCardProps> = ({
  reservation,
}) => {
  const { booking } = reservation;

  if (!booking) {
    return null;
  }

  const { hotel } = booking;

  // Format dates for display
  const formattedCheckIn = format(
    parseISO(reservation.checkInDate),
    'MMMM d, yyyy',
  );
  const formattedCheckOut = format(
    parseISO(reservation.checkOutDate),
    'MMMM d, yyyy',
  );

  // Extract pricing information using the utility function
  const { nightlyRate } = getPricingInfo(booking);
  const subtotal = formatCents(booking.basePrice);
  const estimatedTax = formatCents(booking.estimatedTax);
  const totalDue = formatCents(booking.grandTotalAfterTax);

  // Calculate line items subtotal if available
  const lineItemsSubtotal =
    booking.lineItems?.reduce((total, item) => total + item.price, 0) || 0;

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-emerald-600/30 bg-emerald-50/50 p-4 shadow-lg shadow-emerald-600/10 ring-2 ring-emerald-600/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-6 lg:p-8">
      {/* Subtle accent line */}
      <div className="absolute inset-x-0 top-0 h-1 bg-emerald-600" />

      {/* Confirmed badge */}
      <div className="absolute right-3 top-3 sm:right-6 sm:top-6">
        <div className="inline-flex items-center rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg sm:px-4 sm:py-2 sm:text-sm">
          <div className="mr-1.5 size-1.5 animate-pulse rounded-full bg-white/80 sm:mr-2 sm:size-2" />
          <span>Confirmed Booking</span>
        </div>
      </div>

      {/* Booking info header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center space-x-3">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Reservation #</span>
            <span className="font-bold text-gray-900">
              {reservation.reservationNumber}
            </span>
          </div>
          <div className="text-gray-400">•</div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">Confirmation #</span>
            <span className="font-bold text-gray-900">
              {booking.confirmationNumber}
            </span>
          </div>
        </div>
      </div>

      {/* Hotel information */}
      <div className="mb-6">
        <div className="flex items-start space-x-3 sm:space-x-4">
          <div className="shrink-0 rounded-xl bg-emerald-100 p-2.5 transition-colors duration-200 sm:p-3">
            <BuildingOfficeIcon className="size-5 text-emerald-700 sm:size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="mb-1 text-base font-bold capitalize leading-tight text-gray-900 sm:text-lg">
              {hotel?.name
                ? hotel.name.toLowerCase()
                : 'Hotel Name Not Available'}
            </h4>
            <p className="mb-2 text-sm leading-relaxed text-gray-600">
              {hotel?.formattedAddress || 'Address not available'}
            </p>
            {(hotel?.phone || hotel?.email) && (
              <div className="text-sm text-gray-600">
                {hotel.phone && <div>{hotel.phone}</div>}
                {hotel.email && <div>{hotel.email}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Check-in/out dates */}
      <div className="mb-6 rounded-lg bg-white/60 p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs font-medium text-gray-500">Check-in</span>
            <p className="text-sm font-semibold text-gray-900">
              {formattedCheckIn}
            </p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500">Check-out</span>
            <p className="text-sm font-semibold text-gray-900">
              {formattedCheckOut}
            </p>
          </div>
        </div>
      </div>

      {/* Room Details */}
      <div className="mb-6 rounded-xl border border-gray-200/50 bg-white p-4 sm:p-6">
        <div className="space-y-4 sm:flex sm:items-start sm:justify-between sm:space-y-0">
          <div className="flex-1">
            <h4 className="mb-2 text-lg font-bold text-gray-900 sm:text-xl">
              {booking.roomBeds
                ? formatRoomQuantity(
                    booking.rooms,
                    booking.roomBeds,
                    booking.roomBedType,
                  )
                : `${booking.rooms}x ${booking.roomBedType}`}
            </h4>
            <div className="flex items-center space-x-2 text-gray-600">
              <svg
                className="size-4 shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              <span className="text-sm font-medium">
                Up to {booking.roomBeds} guest
                {booking.roomBeds !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="sm:ml-6 sm:shrink-0 sm:text-right">
            {/* Client Rate */}
            <div className="mb-2">
              <div className="flex items-baseline space-x-1 sm:justify-end">
                <span className="text-xl font-bold text-emerald-700 sm:text-2xl">
                  {formatCents(booking.thsPrice.nightlyPrice)}
                </span>
                <span className="text-xs text-gray-500 sm:text-sm">
                  per night
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-400 sm:text-right">
                + tax
              </div>
            </div>

            <div className="text-sm leading-relaxed text-gray-600">
              <div className="mt-1 text-xs text-gray-500 sm:text-right">
                {booking.nights} nights × {booking.rooms} rooms
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      {booking.lineItems && booking.lineItems.length > 0 && (
        <div className="mb-6">
          <BookingLineItems lineItems={booking.lineItems} />
        </div>
      )}

      {/* Reservation Notes */}
      {reservation.notes && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h5 className="mb-3 text-sm font-semibold text-gray-700">
            Reservation Notes
          </h5>
          <p className="whitespace-pre-wrap text-sm text-gray-900">
            {reservation.notes}
          </p>
        </div>
      )}

      {/* Price Summary */}
      <div className="border-t-2 border-gray-200 pt-6">
        <h4 className="mb-4 font-semibold text-gray-700">Price Summary</h4>

        {/* Per Room Pricing Section */}
        <div className="space-y-3 pt-3">
          <h5 className="text-sm text-gray-500">Per room</h5>
          <div className="flex justify-between text-sm">
            <p>Nightly rate:</p>
            <p>
              {nightlyRate}{' '}
              <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
            </p>
          </div>
          <div className="flex justify-between text-sm">
            <p>Subtotal:</p>
            <p>
              {subtotal}{' '}
              <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
            </p>
          </div>
          <div className="text-xs text-gray-500">
            {nightlyRate} × {booking.nights} night
            {booking.nights > 1 ? 's' : ''}
          </div>
        </div>

        {/* Total Pricing Section */}
        <div className="space-y-3 pt-3">
          <h5 className="text-sm text-gray-500">
            Total ({booking.rooms} room{booking.rooms > 1 ? 's' : ''})
          </h5>
          {booking.lineItems && booking.lineItems.length > 0 && (
            <div className="flex justify-between text-sm">
              <p>Additional Items:</p>
              <p>{formatCents(lineItemsSubtotal)}</p>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <p>Estimated tax:</p>
            <p>{estimatedTax}</p>
          </div>
          <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-bold text-gray-900">
            <p>Total due:</p>
            <p>{totalDue}</p>
          </div>
          <div className="text-xs text-gray-500">
            {subtotal} x {booking.rooms} room{booking.rooms > 1 ? 's' : ''}
            {booking.lineItems &&
              booking.lineItems.length > 0 &&
              ` + ${formatCents(lineItemsSubtotal)} additional items`}{' '}
            + {estimatedTax} tax
          </div>
        </div>
      </div>

      {/* Cancellation policy */}
      {booking.cancellationDetails && (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700">
            Cancellation Policy
          </h5>
          {booking.cancellationDetails.nonRefundable ? (
            <p className="text-sm font-semibold text-red-600">
              Non-refundable booking
            </p>
          ) : booking.cancellationDetails.deadline ? (
            <p className="text-sm text-gray-700">
              Free cancellation until{' '}
              {format(
                parseISO(booking.cancellationDetails.deadline),
                "LLLL do, y 'at' h:mm a",
              )}
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              Please contact us for cancellation terms
            </p>
          )}
          {booking.cancellationDetails.description && (
            <p className="mt-2 text-sm text-gray-600">
              {booking.cancellationDetails.description}
            </p>
          )}
        </div>
      )}

      {/* THS Policy Verbiage */}
      <THSPolicyVerbiage />

      {/* Send Confirmation Email */}
      <div className="mt-6">
        <SendConfirmationEmail booking={booking} />
      </div>
    </div>
  );
};

export default BookingConfirmationCard;
